import type { ReactNode } from 'react'
import { AlertTriangle, Info, ShieldAlert, XCircle } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export type AlertTone = 'info' | 'warning' | 'danger' | 'attribution'

export interface AlertProps {
  tone?: AlertTone
  title: string
  children?: ReactNode
  action?: ReactNode
  className?: string
}

const STYLES: Record<AlertTone, { wrap: string; icon: typeof Info }> = {
  info: { wrap: 'border-info/30 bg-info-subtle text-info', icon: Info },
  warning: { wrap: 'border-warning/30 bg-warning-subtle text-warning', icon: AlertTriangle },
  danger: { wrap: 'border-danger/30 bg-danger-subtle text-danger', icon: XCircle },
  // Reserved for the one warning this product cannot get wrong: a transcript
  // whose speaker names may all be misattributed.
  attribution: { wrap: 'border-warning/40 bg-warning-subtle text-warning', icon: ShieldAlert },
}

/**
 * A persistent, page-level message. Unlike a Toast it does not disappear, so
 * use it for anything the user has to act on or must not miss.
 */
export function Alert({ tone = 'info', title, children, action, className }: AlertProps) {
  const { wrap, icon: Icon } = STYLES[tone]
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex items-start gap-3 rounded-md border px-3.5 py-3', wrap, className)}
    >
      <Icon aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">{title}</p>
        {children && <div className="mt-1 text-base text-fg-muted [&_a]:underline">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}
